import * as React from 'react';
import { twMerge } from 'tailwind-merge';
import { isValidSixDigitHexColor } from '../../../utils/formats/hex-color';
import { Label } from '../../atoms';

export interface IHexColorPickerInputProps {
	label?: string;
	description?: string;
	value: string;
	onChange: (value: string) => void;
	onBlur?: () => void;
	id?: string;
	name?: string;
	disabled?: boolean;
	required?: boolean;
	className?: string;
	fallbackColor?: string;
	'data-testid'?: string;
}

const getDisplayValue = (value: string, fallbackColor?: string) => {
	if (isValidSixDigitHexColor(value)) {
		return value.trim();
	}
	if(fallbackColor && isValidSixDigitHexColor(fallbackColor)) {
		return fallbackColor.trim();
	}
	return undefined;
};

export const HexColorPickerInput = ({
	label,
	description,
	value,
	onChange,
	onBlur,
	id,
	name,
	disabled = false,
	required = false,
	className,
	fallbackColor,
	'data-testid': dataTestId,
}: IHexColorPickerInputProps) => {
	const pickerId = id ?? `${name ?? 'hex-color-picker'}-picker`;
	const displayValue = getDisplayValue(value, fallbackColor);

	return (
		<div className={twMerge('flex w-full flex-col gap-[1rem]', className)} data-testid={dataTestId}>
			{label ? (
				<Label
					label={label}
					description={description}
					weight="medium"
					className={twMerge('cursor-default', label && description && 'gap-[0.4rem]')}
				/>
			) : null}
			<label
				htmlFor={pickerId}
				className={twMerge(
					'flex h-10 w-full cursor-pointer items-center overflow-hidden rounded-md bg-bgFields',
					disabled && 'pointer-events-none cursor-not-allowed opacity-50'
				)}
			>
				<input
					type="color"
					id={pickerId}
					name={name}
					value={displayValue}
					onChange={event => onChange(event.target.value.toUpperCase())}
					onBlur={onBlur}
					disabled={disabled}
					required={required}
					aria-label={label ?? 'Hex color'}
					data-testid={dataTestId ? `${dataTestId}-picker` : undefined}
					className="size-10 shrink-0 border-0 bg-transparent p-0 disabled:cursor-not-allowed [&::-webkit-color-swatch]:size-full [&::-webkit-color-swatch]:rounded-none [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:size-full [&::-webkit-color-swatch-wrapper]:p-0"
				/>
				<div className="flex min-w-0 flex-1 items-center px-[1.2rem]">
					<span
						className="w-full truncate text-sm leading-6 font-normal text-primary select-none"
						data-testid={dataTestId ? `${dataTestId}-input` : undefined}
					>
						{displayValue}
					</span>
				</div>
			</label>
		</div>
	);
};
